import React, { memo } from 'react';
import { Button, Callout, Flex, FromNowDate, Text, Tooltip } from 'core/components';
import { showErrorToast, showSuccessToast } from 'core/components/toast';
import { inject } from 'mobx-react';
import {
  AGENT_TURN_STATUS_ACTIVE,
  AGENT_TURN_STATUS_TYPES,
  AGENT_TURN_STOPPED_MESSAGE
} from '@kentik/ui-shared/kaizen/constants';
import { MessageItem, AgentMessageBubble } from '../KaizenChatSession.styles';
import StepComponent from './StepComponent';
import { getMainThreadSteps } from '../util/turnStepGroups';
import { isDeviceSpecificTurnError } from '../util/toolErrorUtils';
import { isInterruptedTurn, TURN_END_TEXT } from '../util/turnEndText';
import KaizenErrorMessage from './KaizenErrorMessage';

const stepSorter = (a, b) => (a.step_number || 0) - (b.step_number || 0);

const isFinalAnswerStep = (step) =>
  Boolean(step.llm_response?.final_answer) && step.processing_status === 'COMPLETED' && step.parent_step_id === null;

// Memoized agent message component
const AgentMessage = memo(
  ({
    turn,
    isAsker,
    askerName,
    onSelectToolResult,
    onSendToExplorer,
    onShowAgentDataInSidebar,
    isCopilotMode,
    inlineToolResults,
    sessionModel,
    isExistingChatLoading,
    $auth
  }) => {
    const sortedSteps = turn.steps?.slice().sort(stepSorter);
    // Sub-agent steps are omitted here — their work lives in the sub-agent panel,
    // opened from the delegation tool call that summoned them.
    const mainSteps = getMainThreadSteps(sortedSteps);
    const finalAnswerStep = mainSteps.find(isFinalAnswerStep);
    const isTurnActive = AGENT_TURN_STATUS_ACTIVE.includes(turn.processing_status);

    const handleCopyLinkToTurn = () => {
      if (!turn?.id || !navigator?.clipboard?.writeText) {
        return;
      }

      const turnUrl = `${window.location.origin}${window.location.pathname}#kz-turn-${turn.id}`;
      navigator.clipboard
        .writeText(turnUrl)
        .then(() => {
          showSuccessToast('Link copied to clipboard');
        })
        .catch(() => {
          showErrorToast('Unable to copy link');
        });
    };

    // Sits in the footer row, after the copy and rating buttons when there is an answer
    const turnActions = !isTurnActive && (
      <>
        <Tooltip content="Copy link to this answer" position="bottom">
          <Button minimal icon="link" onClick={handleCopyLinkToTurn} />
        </Tooltip>
        {$auth.isSudoer && (
          <Tooltip content="Show data in console" position="bottom">
            <Button minimal icon="info-sign" onClick={() => onShowAgentDataInSidebar(turn, sortedSteps)} />
          </Tooltip>
        )}
        <FromNowDate
          timeStamp={turn.created_at}
          userTimeZone={$auth.userTimezone}
          format="MMM DD, YYYY, hh:mm a"
          tooltipPosition="bottom"
          textOptions={{ ml: 1 }}
        />
      </>
    );

    return (
      <MessageItem key={`agent-${turn.id}`}>
        <AgentMessageBubble id={`kz-turn-${turn.id}`}>
          {mainSteps.map((step, stepIndex) => (
            <StepComponent
              key={`step-${step.id || stepIndex}`}
              $auth={$auth}
              turn={turn}
              isAsker={isAsker}
              askerName={askerName}
              step={step}
              isLatestStep={stepIndex === mainSteps.length - 1}
              onSelectToolResult={onSelectToolResult}
              onSendToExplorer={onSendToExplorer}
              isCopilotMode={isCopilotMode}
              inlineToolResults={inlineToolResults}
              isExistingChatLoading={isExistingChatLoading}
              sessionModel={sessionModel}
              turnActions={step === finalAnswerStep ? turnActions : null}
            />
          ))}

          {(turn.processing_status === AGENT_TURN_STATUS_TYPES.CANCELLED ||
            turn.error_message === AGENT_TURN_STOPPED_MESSAGE) && (
            <Callout intent="warning" icon="disable">
              <Text small>{TURN_END_TEXT[AGENT_TURN_STATUS_TYPES.CANCELLED]}</Text>
            </Callout>
          )}

          {turn.processing_status === AGENT_TURN_STATUS_TYPES.EXPIRED && (
            <Callout intent="warning" icon="time">
              <Text small>{TURN_END_TEXT[AGENT_TURN_STATUS_TYPES.EXPIRED]}</Text>
            </Callout>
          )}

          {isInterruptedTurn(turn) && (
            <Callout intent="warning" icon="offline">
              <Text small>{turn.error_message}</Text>
            </Callout>
          )}

          {turn.error_message && turn.error_message !== AGENT_TURN_STOPPED_MESSAGE && !isInterruptedTurn(turn) && (
            <Callout intent="danger">
              <Text small>
                Error:{' '}
                <KaizenErrorMessage
                  message={turn.error_message}
                  showMessage={isDeviceSpecificTurnError(turn)}
                  fallback="something went wrong"
                />
              </Text>
            </Callout>
          )}

          {!finalAnswerStep && turnActions && (
            <Flex alignItems="center" gap={1}>
              {turnActions}
            </Flex>
          )}
        </AgentMessageBubble>
      </MessageItem>
    );
  }
);

AgentMessage.displayName = 'AgentMessage';

export default inject('$auth')(AgentMessage);
